
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import ProfileSection from "@/lib/settings/ProfileSection";
import SecuritySection from "@/lib/settings/SecuritySection";
import LanguageCurrencySection from "@/lib/settings/LanguageCurrencySection";
import PlatformFeeRateCard from "@/lib/settings/PlatformFeeRateCard";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { TabsHeader } from "@/components/ui/tabs-header";
import { useState } from "react";
import { Settings, Building2, Scale, Image as ImageIcon, Wallet } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useSidebar } from "@/contexts/SidebarContext";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/hooks/useI18n";
import AgencyPage from "@/lib/settings/AgencyPage";
import LegalPage from "@/lib/settings/LegalPage";
import LogoPage from "@/lib/settings/LogoPage";
import PaymentMethodsPage from "@/lib/settings/PaymentMethodsPage";

const UserProfile = () => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userRole } = useAuth();
  const disabledTabs: string[] = ['brand'];
  const tabFromParams = searchParams.get('tab');
  const activeTab = tabFromParams && !disabledTabs.includes(tabFromParams) ? tabFromParams : 'account-settings';
  const { isSidebarCollapsed } = useSidebar();
  
  // Separate edit state for each tab and section
  const [editStates, setEditStates] = useState({
    'profile': false,
    'account-settings-profile': false,
    'account-settings-security': false,
    'account-settings-language': false,
  });
  
  // Saving state for account-settings sections
  const [isSavingSecurityData, setIsSavingSecurityData] = useState(false);
  const [isSavingAvatarData, setIsSavingAvatarData] = useState(false);
  
  // Bank data state
  const [bankData, setBankData] = useState({
    iban: "",
    account_holder: "",
    bank_name: "",
    swift_bic: ""
  });

  const handleBankDataChange = (newData: typeof bankData) => {
    setBankData(newData);
  };

  // Billing data state
  const [billingData, setBillingData] = useState({
    billing_address: "",
    is_company: false,
    vat_number: "",
    fiscal_code: ""
  });

  // Avatar data state
  const [avatarData, setAvatarData] = useState({
    avatar_url: "",
    first_name: "",
    last_name: "",
    email: "",
    role: "Designer",
    status: "Attivo"
  });

  // Tabs configuration
  const tabs = [
    {
      value: "account-settings",
      label: t('settings.accountSettings'),
      icon: Settings
    },
    // Solo chi incassa: il membro del team usa account Stripe e conto dell'agenzia.
    ...(userRole === 'agency' || userRole === 'travel_designer' || userRole === 'admin' ? [{
      value: "payment-methods",
      label: t('paymentOptions.title'),
      icon: Wallet
    }] : []),
    // Tab "Il mio brand" nascosto: non e' usato. BrandPage resta nel codice.
    ...(userRole === 'agency' || userRole === 'admin' ? [{
      value: "agency",
      label: t('settings.agency'),
      icon: Building2
    }] : []),
    ...(userRole === 'travel_designer' || userRole === 'travel_advisor_member'
      || userRole === 'agency' || userRole === 'admin' ? [{
      value: "logo",
      label: t('settings.logo'),
      icon: ImageIcon
    }] : []),
    // Anche il membro del team: vede in sola lettura i testi dell'agenzia, che sono
    // quelli che il suo cliente si trovera' ad accettare.
    ...(userRole === 'travel_designer' || userRole === 'travel_advisor_member'
      || userRole === 'agency' || userRole === 'admin' ? [{
      value: "legal",
      label: t('legalSettings.tabLabel'),
      icon: Scale
    }] : []),
  ];

  const handleBillingDataChange = (data: typeof billingData) => {
    setBillingData(data);
  };

  const handleAvatarDataChange = (data: typeof avatarData) => {
    setAvatarData(data);
  };

  const handleEditToggle = (tabId: string) => {
    setEditStates(prev => ({
      ...prev,
      [tabId]: !prev[tabId as keyof typeof prev]
    }));
  };

  const handleCancelEdit = (tabId: string) => {
    setEditStates(prev => ({
      ...prev,
      [tabId]: false
    }));
    // Reset form data to original values if needed
  };

  const handleSaveAvatarData = async () => {
    setIsSavingAvatarData(true);
    try {
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close edit mode after successful save
      setEditStates(prev => ({
        ...prev,
        'profile': false
      }));
    } catch (error) {
      console.error('Error saving avatar data:', error);
    } finally {
      setIsSavingAvatarData(false);
    }
  };

  const handleSaveSecurityData = async () => {
    setIsSavingSecurityData(true);
    try {
      // TODO: Implement security data save logic
      console.log('Saving security data');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close edit mode after successful save
      setEditStates(prev => ({
        ...prev,
        'account-settings-security': false
      }));
    } catch (error) {
      console.error('Error saving security data:', error);
    } finally {
      setIsSavingSecurityData(false);
    }
  };

  const getCurrentEditState = (tabId: string) => {
    return editStates[tabId as keyof typeof editStates] || false;
  };

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <PrivateLayout
      pageTitle={t('settings.profile')}
      pageDescription={t('settings.profileDescription')}
    >
      <div className="space-y-4 mb-8 mt-2">
        {/* Reusable Tabs Header */}
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />

        {/* Tab Content */}
        <div className="max-w-6xl mx-auto pt-2">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <ProfileTabsContent value="account-settings">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ProfileSection
                  isEditing={getCurrentEditState("account-settings-profile")}
                  onEditToggle={() =>
                    handleEditToggle("account-settings-profile")
                  }
                  onCancelEdit={() =>
                    handleCancelEdit("account-settings-profile")
                  }
                />
                <LanguageCurrencySection
                  isEditing={getCurrentEditState("account-settings-language")}
                  onEditToggle={() =>
                    handleEditToggle("account-settings-language")
                  }
                  onCancelEdit={() =>
                    handleCancelEdit("account-settings-language")
                  }
                />
                <SecuritySection
                  isEditing={getCurrentEditState("account-settings-security")}
                  onEditToggle={() =>
                    handleEditToggle("account-settings-security")
                  }
                  onCancelEdit={() =>
                    handleCancelEdit("account-settings-security")
                  }
                  isSaving={isSavingSecurityData}
                  onSave={handleSaveSecurityData}
                />
                {/* L'indipendente non ha la tab Agenzia: la sua aliquota sta qui. */}
                {userRole === 'travel_designer' && <PlatformFeeRateCard />}
              </div>
            </ProfileTabsContent>

            <ProfileTabsContent value="payment-methods">
              <PaymentMethodsPage />
            </ProfileTabsContent>

            <ProfileTabsContent value="agency">
              <AgencyPage />
            </ProfileTabsContent>

            <ProfileTabsContent value="logo">
              <LogoPage />
            </ProfileTabsContent>

            <ProfileTabsContent value="legal">
              <LegalPage />
            </ProfileTabsContent>
          </Tabs>
        </div>
      </div>
    </PrivateLayout>
  );
};

export default UserProfile;
